import React, { useState, useEffect, useMemo } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
  DropdownItem,
  Badge,
  Input,
  Label,
  Spinner,
  Modal,
  ModalBody,
} from "reactstrap";
import {
  Block,
  BlockHead,
  BlockBetween,
  BlockHeadContent,
  BlockTitle,
  BlockDes,
  Icon,
  Button,
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableRow,
  DataTableItem,
  PaginationComponent,
  RSelect,
  PreviewCard,
  Row,
  Col,
} from "../../../components/Component";
import { api } from "../../../utils/api";
import { toast } from "react-toastify";

const WorkflowHistoryPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ status: "all" });
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemPerPage] = useState(15);
  const [totalItems, setTotalItems] = useState(0);
  const [viewModal, setViewModal] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);

  useEffect(() => {
    loadHistory();
  }, [filters, currentPage]);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        per_page: itemPerPage,
      };
      
      if (filters.status && filters.status !== "all") {
        params.status = filters.status;
      }

      const response = await api.getAllWorkflowHistory(params);
      
      // Gérer la pagination Laravel
      if (response.data && Array.isArray(response.data)) {
        setData(response.data);
        setTotalItems(response.total || response.data.length);
      } else if (Array.isArray(response)) {
        setData(response);
        setTotalItems(response.length);
      } else {
        setData([]);
        setTotalItems(0);
      }
    } catch (error) {
      toast.error("Erreur lors du chargement de l'historique");
      setData([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      submitted: { color: "secondary", label: "Soumise" },
      received: { color: "info", label: "Reçue" },
      in_analysis: { color: "warning", label: "En analyse" },
      corrective_action_requested: { color: "danger", label: "Action requise" },
      action_completed: { color: "success", label: "Action réalisée" },
      verified: { color: "primary", label: "Vérifiée" },
      closed: { color: "dark", label: "Clôturée" },
    };
    const badge = badges[status] || { color: "secondary", label: status };
    return <Badge color={badge.color}>{badge.label}</Badge>;
  };

  // Filtrer les données selon la recherche
  const filteredData = useMemo(() => {
    if (!searchText) return data;
    
    return data.filter((item) => {
      const observationTitle = item.observation?.title?.toLowerCase() || "";
      const action = item.action?.toLowerCase() || "";
      const comments = item.comments?.toLowerCase() || "";
      const userName = item.user 
        ? `${item.user.first_name || ""} ${item.user.last_name || ""}`.toLowerCase().trim() || item.user.email?.toLowerCase() || ""
        : "";
      
      const searchLower = searchText.toLowerCase();
      return (
        observationTitle.includes(searchLower) ||
        action.includes(searchLower) ||
        comments.includes(searchLower) ||
        userName.includes(searchLower)
      );
    });
  }, [data, searchText]);

  // Utiliser directement les données du backend (déjà paginées)
  const currentItems = filteredData;

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const openViewModal = (action) => {
    setSelectedAction(action);
    setViewModal(true);
  };

  return (
    <React.Fragment>
      <Head title="Historique Workflow" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Historique Workflow</BlockTitle>
              <BlockDes className="text-soft">
                {totalItems} action(s) au total
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <div className="toggle-wrap nk-block-tools-toggle">
                <UncontrolledDropdown>
                  <DropdownToggle tag="a" className="dropdown-toggle btn btn-white btn-dim btn-outline-light">
                    <Icon name="filter-alt" className="d-none d-sm-inline"></Icon>
                    <span>Filtrer</span>
                    <Icon name="chevron-right" className="dd-indc"></Icon>
                  </DropdownToggle>
                  <DropdownMenu end>
                    <ul className="link-list-opt no-bdr">
                      <li>
                        <DropdownItem
                          tag="a"
                          href="#dropdownitem"
                          onClick={(ev) => {
                            ev.preventDefault();
                            setFilters({ ...filters, status: "all" });
                            setCurrentPage(1);
                          }}
                        >
                          <span>Tous</span>
                        </DropdownItem>
                      </li>
                      <li>
                        <DropdownItem
                          tag="a"
                          href="#dropdownitem"
                          onClick={(ev) => {
                            ev.preventDefault();
                            setFilters({ ...filters, status: "submitted" });
                            setCurrentPage(1);
                          }}
                        >
                          <span>Soumises</span>
                        </DropdownItem>
                      </li>
                      <li>
                        <DropdownItem
                          tag="a"
                          href="#dropdownitem"
                          onClick={(ev) => {
                            ev.preventDefault();
                            setFilters({ ...filters, status: "received" });
                            setCurrentPage(1);
                          }}
                        >
                          <span>Reçues</span>
                        </DropdownItem>
                      </li>
                      <li>
                        <DropdownItem
                          tag="a"
                          href="#dropdownitem"
                          onClick={(ev) => {
                            ev.preventDefault();
                            setFilters({ ...filters, status: "in_analysis" });
                            setCurrentPage(1);
                          }}
                        >
                          <span>En analyse</span>
                        </DropdownItem>
                      </li>
                      <li>
                        <DropdownItem
                          tag="a"
                          href="#dropdownitem"
                          onClick={(ev) => {
                            ev.preventDefault();
                            setFilters({ ...filters, status: "corrective_action_requested" });
                            setCurrentPage(1);
                          }}
                        >
                          <span>Action requise</span>
                        </DropdownItem>
                      </li>
                      <li>
                        <DropdownItem
                          tag="a"
                          href="#dropdownitem"
                          onClick={(ev) => {
                            ev.preventDefault();
                            setFilters({ ...filters, status: "action_completed" });
                            setCurrentPage(1);
                          }}
                        >
                          <span>Action réalisée</span>
                        </DropdownItem>
                      </li>
                      <li>
                        <DropdownItem
                          tag="a"
                          href="#dropdownitem"
                          onClick={(ev) => {
                            ev.preventDefault();
                            setFilters({ ...filters, status: "verified" });
                            setCurrentPage(1);
                          }}
                        >
                          <span>Vérifiées</span>
                        </DropdownItem>
                      </li>
                      <li>
                        <DropdownItem
                          tag="a"
                          href="#dropdownitem"
                          onClick={(ev) => {
                            ev.preventDefault();
                            setFilters({ ...filters, status: "closed" });
                            setCurrentPage(1);
                          }}
                        >
                          <span>Clôturées</span>
                        </DropdownItem>
                      </li>
                    </ul>
                  </DropdownMenu>
                </UncontrolledDropdown>
              </div>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <div className="mb-3">
            <Row className="g-2">
              <Col md="6">
                <div className="form-control-wrap">
                  <div className="form-icon form-icon-left">
                    <Icon name="search"></Icon>
                  </div>
                  <Input
                    type="text"
                    className="form-control"
                    placeholder="Rechercher dans l'historique..."
                    value={searchText}
                    onChange={(e) => {
                      setSearchText(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>
              </Col>
            </Row>
          </div>

          {loading ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
            </div>
          ) : filteredData.length === 0 ? (
            <PreviewCard>
              <div className="text-center py-4">
                <span className="text-soft">Aucun historique trouvé</span>
              </div>
            </PreviewCard>
          ) : (
            <DataTable className="card-stretch workflow-hse-table">
              <DataTableBody>
                <DataTableHead className="nk-tb-item nk-tb-head">
                  <DataTableRow>
                    <span className="sub-text">Observation</span>
                  </DataTableRow>
                  <DataTableRow size="xl">
                    <span className="sub-text">Statut</span>
                  </DataTableRow>
                  <DataTableRow size="xxl">
                    <span className="sub-text">Action</span>
                  </DataTableRow>
                  <DataTableRow size="xxl">
                    <span className="sub-text">Utilisateur</span>
                  </DataTableRow>
                  <DataTableRow size="md">
                    <span className="sub-text">Date</span>
                  </DataTableRow>
                  <DataTableRow className="nk-tb-col-tools text-end">
                    <span className="sub-text">Actions</span>
                  </DataTableRow>
                </DataTableHead>
                {currentItems.map((item) => (
                  <DataTableItem key={item.id}>
                    <DataTableRow>
                      <div>
                        <h6 className="title" style={{ maxWidth: "250px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={item.observation?.title}>
                          {item.observation?.title || "Observation supprimée"}
                        </h6>
                        {item.observation?.site && (
                          <span className="sub-text">{item.observation.site.name}</span>
                        )}
                      </div>
                    </DataTableRow>
                    <DataTableRow size="xl">
                      {getStatusBadge(item.status)}
                    </DataTableRow>
                    <DataTableRow size="xxl">
                      <span style={{ maxWidth: "200px", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={item.action || "-"}>
                        {item.action || "-"}
                      </span>
                    </DataTableRow>
                    <DataTableRow size="xxl">
                      <span>
                        {item.user
                          ? `${item.user.first_name || ""} ${item.user.last_name || ""}`.trim() || item.user.email
                          : "Système"}
                      </span>
                    </DataTableRow>
                    <DataTableRow size="md">
                      <span style={{ whiteSpace: "nowrap" }}>
                        {item.created_at ? new Date(item.created_at).toLocaleString("fr-FR") : "-"}
                      </span>
                    </DataTableRow>
                    <DataTableRow className="nk-tb-col-tools text-end">
                      <ul className="nk-tb-actions gx-1">
                        <li>
                          <Button
                            color="light"
                            size="sm"
                            onClick={() => openViewModal(item)}
                            className="btn-icon"
                            title="Voir les détails"
                          >
                            <Icon name="eye"></Icon>
                          </Button>
                        </li>
                      </ul>
                    </DataTableRow>
                  </DataTableItem>
                ))}
              </DataTableBody>
              <div className="card-inner">
                {totalItems > 0 && (
                  <PaginationComponent
                    itemPerPage={itemPerPage}
                    totalItems={totalItems}
                    paginate={paginate}
                    currentPage={currentPage}
                  />
                )}
              </div>
            </DataTable>
          )}
        </Block>

        {/* Modal pour voir les détails */}
        <Modal isOpen={viewModal} toggle={() => setViewModal(false)} className="modal-dialog-centered modal-lg">
          <div className="modal-header">
            <h5 className="modal-title">Détails de l'action</h5>
            <button className="close" onClick={() => setViewModal(false)}>
              <Icon name="cross"></Icon>
            </button>
          </div>
          <ModalBody>
            {selectedAction && (
              <div className="nk-modal-details">
                <div className="nk-block nk-block-md">
                  <h6 className="text-soft mb-3">
                    <Icon name="info" className="me-2"></Icon>
                    Informations principales
                  </h6>
                  <div className="row g-3">
                    <div className="col-12">
                      <div className="form-group">
                        <label className="form-label">Observation</label>
                        <div className="form-control-plaintext">
                          {selectedAction.observation?.title || "Observation supprimée"}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">Statut</label>
                        <div className="form-control-plaintext">
                          {getStatusBadge(selectedAction.status)}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">Site</label>
                        <div className="form-control-plaintext">
                          {selectedAction.observation?.site?.name || "-"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {selectedAction.action && (
                  <div className="nk-block nk-block-md mt-4">
                    <h6 className="text-soft mb-3">
                      <Icon name="file-text" className="me-2"></Icon>
                      Action
                    </h6>
                    <div className="form-group">
                      <div className="form-control-plaintext" style={{ whiteSpace: "pre-wrap" }}>
                        {selectedAction.action}
                      </div>
                    </div>
                  </div>
                )}

                {selectedAction.comments && (
                  <div className="nk-block nk-block-md mt-4">
                    <h6 className="text-soft mb-3">
                      <Icon name="comment" className="me-2"></Icon>
                      Commentaires
                    </h6>
                    <div className="form-group">
                      <div className="form-control-plaintext" style={{ whiteSpace: "pre-wrap" }}>
                        {selectedAction.comments}
                      </div>
                    </div>
                  </div>
                )}

                <div className="nk-block nk-block-md mt-4">
                  <h6 className="text-soft mb-3">
                    <Icon name="user" className="me-2"></Icon>
                    Informations complémentaires
                  </h6>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">Effectué par</label>
                        <div className="form-control-plaintext">
                          {selectedAction.user
                            ? `${selectedAction.user.first_name || ""} ${selectedAction.user.last_name || ""}`.trim() || selectedAction.user.email
                            : "Système"}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">Date</label>
                        <div className="form-control-plaintext">
                          {selectedAction.created_at ? new Date(selectedAction.created_at).toLocaleString("fr-FR") : "-"}
                        </div>
                      </div>
                    </div>
                    {selectedAction.due_date && (
                      <div className="col-md-6">
                        <div className="form-group">
                          <label className="form-label">Date d'échéance</label>
                          <div className="form-control-plaintext">
                            {new Date(selectedAction.due_date).toLocaleDateString("fr-FR")}
                          </div>
                        </div>
                      </div>
                    )}
                    {selectedAction.completed_at && (
                      <div className="col-md-6">
                        <div className="form-group">
                          <label className="form-label">Date de complétion</label>
                          <div className="form-control-plaintext">
                            {new Date(selectedAction.completed_at).toLocaleString("fr-FR")}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            <div className="text-end mt-4">
              <Button color="light" onClick={() => setViewModal(false)}>
                Fermer
              </Button>
            </div>
          </ModalBody>
        </Modal>
      </Content>
    </React.Fragment>
  );
};

export default WorkflowHistoryPage;

